import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';

function getSettingCategory(key: string): string {
  if (key.startsWith('sheets_')) return 'sheets';
  if (key.startsWith('tax_')) return 'tax';
  if (key.startsWith('business_')) return 'business';
  if (key.startsWith('printer_')) return 'printer';
  if (key.startsWith('onboarding_')) return 'onboarding';
  if (key.startsWith('_')) return '_system';
  return 'general';
}

export function useSettingsStore() {
  const db = useDatabase();
  const [settingsCache, setSettingsCache] = useState<Record<string, unknown>>({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const subscription = db.settings.find().$.subscribe((docs: Array<{ key: string; value: unknown }>) => {
      const cache: Record<string, unknown> = {};
      docs.forEach((doc: { key: string; value: unknown }) => {
        cache[doc.key] = doc.value;
      });
      setSettingsCache(cache);
      setIsReady(true);
    });

    return () => subscription.unsubscribe();
  }, [db.settings]);

  const getSetting = useCallback(
    <T = unknown>(key: string): T | undefined => {
      return settingsCache[key] as T | undefined;
    },
    [settingsCache]
  );

  const setSetting = useCallback(
    async (key: string, value: unknown): Promise<{ success: boolean }> => {
      const previousValue = settingsCache[key];
      
      setSettingsCache(prev => ({ ...prev, [key]: value }));
      
      try {
        const now = new Date().toISOString();
        await db.settings.upsert({
          id: key,
          key,
          value,
          category: getSettingCategory(key),
          updatedAt: now,
        });
        return { success: true };
      } catch (error) {
        setSettingsCache(prev => ({ ...prev, [key]: previousValue }));
        console.error(`Failed to save setting ${key}:`, error);
        return { success: false };
      }
    },
    [db.settings, settingsCache]
  );

  const deleteSetting = useCallback(
    async (key: string): Promise<{ success: boolean }> => {
      const previousValue = settingsCache[key];
      
      setSettingsCache(prev => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
      
      try {
        const doc = await db.settings.findOne(key).exec();
        if (doc) {
          await doc.remove();
        }
        return { success: true };
      } catch (error) {
        setSettingsCache(prev => ({ ...prev, [key]: previousValue }));
        console.error(`Failed to delete setting ${key}:`, error);
        return { success: false };
      }
    },
    [db.settings, settingsCache]
  );

  const getAllSettings = useCallback(
    (category?: string): Record<string, unknown> => {
      if (!category) return { ...settingsCache };
      return Object.fromEntries(
        Object.entries(settingsCache).filter(([key]) => getSettingCategory(key) === category)
      );
    },
    [settingsCache]
  );

  return {
    getSetting,
    setSetting,
    deleteSetting,
    getAllSettings,
    isReady,
  };
}

export type SettingsStore = ReturnType<typeof useSettingsStore>;
